import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import type { RootState } from "@/redux/store";
import {
  connectChatSocket,
  disconnectChatSocket,
  getChatSocket,
  type ChatSocketAuth,
} from "@/services/chatSocketService";

type JwtMaybe = {
  userId?: string;
  sub?: string;
  id?: string;
  _id?: string;
};

function getUserIdFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const decoded = jwtDecode<JwtMaybe>(token);
    return decoded.userId ?? decoded.sub ?? decoded.id ?? decoded._id ?? null;
  } catch {
    return null;
  }
}

export function useChatSocket(enabled: boolean) {
  const auth = useSelector((state: RootState) => state.auth);
  const token = auth?.token ?? null;
  const userId = useMemo(() => getUserIdFromToken(token), [token]);

  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (!token || !userId) return;

    const socket = connectChatSocket({ token, userId } as ChatSocketAuth);

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onDisconnect);

    setConnected(socket.connected);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onDisconnect);
    };
  }, [enabled, token, userId]);

  return {
    socket: getChatSocket(),
    connected,
    token,
    userId,
    disconnect: disconnectChatSocket,
  };
}

